import { IcAlert, IcChevron, IcSentMail } from '@assets/icons/24x24';
import { Center, For, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectPlatformByCode } from '@redux/config/config.selectors';
import { getWorstAlertLevelColor } from '@utils/data/notification.utils';
import { createContext, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { NotificationDetails, NotificationPlatformDetail, NotificationTypeEnum } from '@/types/data/data.types';

import { AlertModal } from './AlertModal';

type PlatformRowProps = {
  code: string;
  detail: NotificationPlatformDetail;
  type: NotificationTypeEnum;
  onClick: () => void;
};

function PlatformRow({ code, detail, type, onClick }: Readonly<PlatformRowProps>) {
  const { formatMessage } = useIntl();
  const platform = useAppSelector((state) => selectPlatformByCode(state, code));
  const platformName = platform?.name ?? formatMessage({ id: 'notification.message.unknownPlatform' });
  return (
    <HStack
      width="100%"
      height="64px"
      paddingLeft={3}
      paddingRight={2}
      gap={3}
      cursor="pointer"
      onClick={onClick}
      backgroundColor="neutral.700"
      className="group"
      _hover={{ backgroundColor: 'neutral.600' }}
    >
      <Text marginRight="auto" lineClamp={1}>
        {platformName}
      </Text>
      <HStack gap={1} width="64px">
        <Icon boxSize="24px" as={IcAlert} color={getWorstAlertLevelColor(detail.alerts)} />
        <Text color="skyblue">{detail.alerts.length}</Text>
      </HStack>
      {type === NotificationTypeEnum.DRONE && (
        <HStack gap={1} width="64px">
          <Icon boxSize="24px" as={IcSentMail} color="neutral.300" />
          <Text color="skyblue">{detail.messages.length}</Text>
        </HStack>
      )}
      <Icon as={IcChevron} transform="rotate(180deg)" color="neutral.300" _groupHover={{ color: 'skyblue' }} />
    </HStack>
  );
}

interface AlertDetailsContextType {
  detailsByPlatform: Map<string, NotificationPlatformDetail>;
  selectedPlatform: string | null;
  setSelectedPlatform: (value: string | null) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
}

const AlertDetailsContext = createContext<AlertDetailsContextType | undefined>(undefined);

export const useAlertDetailsContext = (): AlertDetailsContextType => {
  const context = useContext(AlertDetailsContext);
  if (!context) {
    throw new Error('useAlertDetailsContext must be used within a AlertDetailsContext.Provider');
  }
  return context;
};

type Props = {
  notificationDetails: NotificationDetails;
  isFetching: boolean;
};

export default function AlertsDetail({ notificationDetails, isFetching }: Readonly<Props>) {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const contextValues = useMemo(
    () => ({
      detailsByPlatform: new Map(Object.entries(notificationDetails.detailsByPlatform)),
      selectedPlatform,
      setSelectedPlatform,
      open,
      setOpen,
    }),
    [notificationDetails.detailsByPlatform, open, selectedPlatform],
  );

  return (
    <AlertDetailsContext.Provider value={contextValues}>
      <AlertModal type={notificationDetails.notification.type} />
      {isFetching ? (
        <Center width="100%" height="100%" backgroundColor="neutral.700">
          <Spinner color="neutral.white" height="70px" width="70px" />
        </Center>
      ) : (
        <ScrollArea backgroundColor="neutral.850">
          <VStack width="100%" height="100%" gap={0.25}>
            <For each={Object.entries(notificationDetails.detailsByPlatform)}>
              {([platformCode, detail]) => (
                <PlatformRow
                  key={platformCode}
                  code={platformCode}
                  detail={detail}
                  type={notificationDetails.notification.type}
                  onClick={() => {
                    setSelectedPlatform(platformCode);
                    setOpen(true);
                  }}
                />
              )}
            </For>
          </VStack>
        </ScrollArea>
      )}
    </AlertDetailsContext.Provider>
  );
}
